import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Trash2, Users, Phone, Mail, StickyNote, Gift, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const DAYS = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const PAY = { deposit: "Záloha", full: "Celá částka", gift_voucher: "Poukaz", free: "Zdarma" };

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtCreated(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const parts = new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Prague" }).formatToParts(d);
    const g = (t) => parts.find((p) => p.type === t)?.value;
    return `${g("day")}. ${g("month")}. ${g("year")} v ${g("hour")}:${g("minute")}`;
  } catch { return null; }
}

export default function AdminReservationCalendar({ reservations = [], onDelete }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(ymd(today));

  const byDate = useMemo(() => {
    const m = {};
    for (const r of reservations) {
      if (!r.date) continue;
      (m[r.date] = m[r.date] || []).push(r);
    }
    for (const k in m) m[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return m;
  }, [reservations]);

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const move = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  const selectedList = byDate[selected] || [];
  const monthTotal = reservations.filter((r) => (r.date || "").startsWith(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`)).length;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="reservation-calendar">
      <div className="lg:col-span-2 border border-white/10 bg-[#0d0d0d] p-4">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="p-2 text-neutral-400 hover:text-nv"><ChevronLeft className="w-5 h-5" /></button>
          <h3 className="font-head uppercase tracking-widest text-neutral-100 text-lg">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
          <button data-testid="cal-next" onClick={() => move(1)} className="p-2 text-neutral-400 hover:text-nv"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="text-[11px] text-neutral-500 uppercase tracking-widest mb-2 text-center">Rezervací tento měsíc: <span className="text-nv">{monthTotal}</span></div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAYS.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-widest text-neutral-600 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="aspect-square" />;
            const key = ymd(d);
            const list = byDate[key] || [];
            const isToday = key === ymd(today);
            const isSel = key === selected;
            const confirmed = list.filter((r) => r.status === "confirmed").length;
            const pending = list.length - confirmed;
            return (
              <button key={i} data-testid={`cal-day-${key}`} onClick={() => setSelected(key)}
                className={`aspect-square p-1 flex flex-col items-center justify-start border transition-colors text-xs
                  ${isSel ? "border-red-600 bg-red-950/30" : "border-white/5 hover:border-white/20 bg-[#111]"}`}>
                <span className={`${isToday ? "text-nv font-bold" : "text-neutral-300"} text-[11px]`}>{d.getDate()}</span>
                {list.length > 0 && (
                  <div className="mt-auto flex flex-wrap gap-0.5 justify-center pb-0.5">
                    {confirmed > 0 && <span className="text-[9px] px-1 rounded-full bg-green-700/40 text-green-400">{confirmed}</span>}
                    {pending > 0 && <span className="text-[9px] px-1 rounded-full bg-yellow-700/40 text-yellow-400">{pending}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-[10px] text-neutral-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600 inline-block" />Potvrzeno</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />Čeká na platbu</span>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="cal-day-detail">
        <h4 className="font-head uppercase tracking-widest text-neutral-200 mb-3 text-sm">
          {selected.split("-").reverse().join(". ")} — {selectedList.length} {selectedList.length === 1 ? "rezervace" : "rezervací"}
        </h4>
        {selectedList.length === 0 && <p className="text-sm text-neutral-600">Na tento den nejsou žádné rezervace.</p>}
        <div className="space-y-3">
          {selectedList.map((r) => (
            <div key={r.id} data-testid={`cal-res-${r.id}`} className="border border-white/10 bg-[#111] p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-nv" />
                  <span className="font-head text-neutral-100">{r.time}</span>
                  {r.status === "confirmed"
                    ? <span className="text-[10px] px-1.5 rounded-full bg-green-700/40 text-green-400">Potvrzeno</span>
                    : <span className="text-[10px] px-1.5 rounded-full bg-yellow-700/40 text-yellow-400">Čeká</span>}
                </div>
                {onDelete && <button data-testid={`cal-del-${r.id}`} onClick={() => onDelete(r.id)} className="text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="mt-2 text-sm text-neutral-200">{r.first_name} {r.last_name}</div>
              <div className="mt-1 space-y-0.5 text-[12px] text-neutral-400">
                <div className="flex items-center gap-2"><Users className="w-3 h-3 text-neutral-500" />{r.num_players || "?"} {(r.num_players === 1) ? "hráč" : "hráčů"}</div>
                {r.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-neutral-500" /><a href={`tel:${r.phone}`} className="hover:text-nv">{r.phone}</a></div>}
                {r.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-neutral-500" /><a href={`mailto:${r.email}`} className="hover:text-nv break-all">{r.email}</a></div>}
                <div className="flex items-center gap-2"><Gift className="w-3 h-3 text-neutral-500" />{PAY[r.payment_method] || "-"}{r.amount ? ` · ${r.amount} Kč` : ""}</div>
                {r.note && <div className="flex items-start gap-2"><StickyNote className="w-3 h-3 text-neutral-500 mt-0.5" /><span className="italic">{r.note}</span></div>}
                {fmtCreated(r.created_at) && <div className="flex items-center gap-2 text-neutral-600 text-[11px] pt-1" data-testid={`cal-res-created-${r.id}`}><Clock className="w-3 h-3 text-neutral-600" />Vytvořeno: {fmtCreated(r.created_at)}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
